<script lang="ts">
	//# SVELTE
	import { onMount } from 'svelte';
	//# Modules
	import { format } from 'svelte-i18n';
	//# Services
	import { user_data } from 'src/stores/account/user';
	//# Helpers
	import { capFirstChar } from 'src/helpers/utilities';
	import { isSubUnit } from 'src/helpers/user_helpers';
	//# Design system components
	import { Container, Button, Text, Modal, Alert, Input } from '@Shelly/shelly-design-system';
	import { mobile } from 'src/services/mobile-rework';
	import sha1 from 'crypto-js/sha1';
	import { HttpClient } from 'src/services/utils/http-client';

	export let showModal = false;
	export let loading = false;
	export let inst = '';
	export let token = '';
	export let callbackSuccess = () => {};
	export let callbackError = (error) => {};

	let passInput = '';
	let passInputErr = '';

	$: unitCount = Object.keys($user_data.available_units ?? {}).length;
	$: isSubAccount = isSubUnit();

	const handleDeleteAccount = async () => {
		loading = true;

		let hashPassInput = sha1(passInput).toString();

		if ($user_data.password === hashPassInput) {
			const config = {
				data: null,
				headers: {
					'Content-Type': 'application/json',
					Authorization: 'Bearer ' + token,
				},
			};
			try {
				const res = await new HttpClient(inst).post(`/user/delete_user`, undefined, config);
				if (res.data.isok) {
					await callbackSuccess();
				}
			} catch (error) {
				callbackError(error);
			}
		} else {
			passInputErr = $format('input_error_incorrect_password', {
				default: 'Incorrect password',
			});
		}

		loading = false;
	};

	const clearError = () => {
		passInputErr = '';
	};

	const handleCloseModal = () => {
		loading = false;
		showModal = false;
	};

	const closeModal = (e: CustomEvent) => {
		if (showModal) {
			e.preventDefault();
			handleCloseModal();
		}
	};

	onMount(() => {
		if (mobile.isMobile) {
			mobile.addEventListener('back_button', closeModal);
			return () => mobile.removeEventListener('back_button', closeModal);
		}
	});
</script>

{#if showModal}
	<Modal
		on:close={closeModal}
		{showModal}
		title={`${capFirstChar(
			$format('button_delete', { default: 'Delete' }),
		)} ${capFirstChar($format('label_account', { default: 'Acount' }))}?`}
		backdropClose={!loading}
		removeScrollPadding
	>
		<Container slot="content" class="pb-0" padding="scrollPaddings">
			<Text class="width-md-auto width-sm-full mb-m">
				{#if isSubAccount}
					{$format('deleting_sub_account_msg', {
						default:
							'Deleting your account will remove your access to all shared homes. Any personal data associated with your account will also be permanently deleted. Please note that once deleted, your profile cannot be recovered.',
					})}
				{:else if $user_data.premium}
					{$format('deleting_premium_account_multi_unit_msg', {
						default:
							'Deleting your account will permanently remove all your homes ({unitCount} home(s)), including all devices, scenes, and data. Any users you have invited to your homes will lose access. Active Premium or Free trial subscriptions will be terminated, and paid subscription fees for the rest of the subscription period are non-refundable. Please note that once deleted, your profile cannot be restored.',
						values: { unitCount },
					})}
				{:else}
					{$format('deleting_account_multi_unit_msg', {
						default:
							'Deleting your account will permanently remove all your homes ({unitCount} home(s)), including all devices, scenes, and data. Any users you have invited to your homes will lose access. Please note that once deleted, your profile cannot be recovered.',
						values: { unitCount },
					})}
				{/if}
			</Text>
			<Alert
				class="mb-s"
				type="warning"
				heading={$format('confirm_deletion_request_msg', {
					default: 'Please confirm your deletion request by entering your account password.',
				})}
			/>
			<Text styleType="h2" class="mb-s">
				{capFirstChar($format('placeholder_password'))}
			</Text>
			<Input
				id="input-label_password"
				aria-labelledby="input-label_password"
				bind:value={passInput}
				on:click={() => clearError()}
				error={passInputErr}
				size="l"
				label={capFirstChar($format('label_password'))}
				type="password"
				class="width-md-l widht-sm-full mb-sm-m mb-md-l"
				a11yProps={{
					iconButtonAriaLabel: $format('button_show_password', {
						default: 'Show password',
					}),
				}}
			/>
		</Container>
		<div slot="footer" class="d-flex justify-content-end">
			<Button
				variant="secondary-2"
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
				responsiveSize={{
					sm: 'm',
				}}
				disabled={loading}
				on:click={handleCloseModal}
			>
				{capFirstChar(
					$format('button_cancel', {
						default: 'cancel',
					}),
				)}
			</Button>
			<Button
				variant="danger"
				{loading}
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
				responsiveSize={{
					sm: 'm',
				}}
				on:click={handleDeleteAccount}
			>
				{capFirstChar(
					$format('button_delete_account', {
						default: 'Delete account',
					}),
				)}
			</Button>
		</div>
	</Modal>
{/if}
